import { Loader2 } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import type { OrgPluginInstallationStatus } from '@/lib/types';

interface PluginInstallationStatusPillProps {
  status: OrgPluginInstallationStatus;
  /** When `true`, render the system-plugin variant ("Required"). */
  system?: boolean;
}

/**
 * Visual status indicator shown on every plugin card and detail header.
 * Drives a `data-status` attribute so stories can assert on state without
 * coupling to copy/styling.
 */
export function PluginInstallationStatusPill({
  status,
  system,
}: PluginInstallationStatusPillProps) {
  if (system) {
    return (
      <Badge
        variant="outline"
        data-testid="plugin-installation-status"
        data-status="system"
        className="gap-1"
      >
        Required
      </Badge>
    );
  }
  if (status === 'installed') {
    return (
      <Badge
        variant="default"
        data-testid="plugin-installation-status"
        data-status="installed"
        className="gap-1"
      >
        Installed
      </Badge>
    );
  }
  if (status === 'installing' || status === 'uninstalling') {
    return (
      <Badge
        variant="secondary"
        data-testid="plugin-installation-status"
        data-status={status}
        className="gap-1"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        {status === 'installing' ? 'Installing…' : 'Uninstalling…'}
      </Badge>
    );
  }
  // uninstalled → "Available" reads better than "Uninstalled" for a row
  // the user is being shown a chance to install.
  return (
    <Badge
      variant="outline"
      data-testid="plugin-installation-status"
      data-status="uninstalled"
      className="gap-1"
    >
      Available
    </Badge>
  );
}
